/**
 * F-131 — roadmap data hooks (React Query) over api/roadmap.ts.
 *
 * Keys live under ['roadmap', …] so one invalidation of ['roadmap', orgId] refreshes the org's
 * access, result, items and snapshots together. The points catalogue is global content and is
 * cached for an hour. Nothing here is persisted to IndexedDB (main.tsx allow-lists the keys it
 * dehydrates; roadmap keys are not among them), so a refresh never shows yesterday's state.
 */
import { useEffect, useRef, useState } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type RoadmapAnswer,
  RoadmapForbiddenError,
  type RoadmapResult,
  answerRoadmapQuestion,
  fetchRoadmapAccess,
  fetchRoadmapItems,
  fetchRoadmapPoints,
  fetchRoadmapResult,
  fetchRoadmapSnapshots,
  recomputeRoadmapState,
} from '@/api/roadmap';
import { currentMoveQueryKey } from './coaching-query-keys';

export const roadmapKeys = {
  points: ['roadmap', 'points'] as const,
  org: (orgId: string | null) => ['roadmap', orgId ?? 'none'] as const,
  access: (orgId: string | null) => ['roadmap', orgId ?? 'none', 'access'] as const,
  result: (orgId: string | null) => ['roadmap', orgId ?? 'none', 'result'] as const,
  items: (orgId: string | null) => ['roadmap', orgId ?? 'none', 'items'] as const,
  snapshots: (orgId: string | null) => ['roadmap', orgId ?? 'none', 'snapshots'] as const,
};

/** Never retry a refusal: 42501 is an answer, not a blip. */
function retryUnlessForbidden(failureCount: number, error: unknown): boolean {
  if (error instanceof RoadmapForbiddenError) return false;
  return failureCount < 2;
}

/**
 * `enabled` is the viewer's roadmap access. The catalogue is a paid read (migration 596), so
 * asking without access is a 403 on every page load: Free orgs opening Home did exactly that
 * after the flags opened to everyone on 23 Sep 2026.
 */
export function useRoadmapPoints(enabled: boolean) {
  return useQuery({
    queryKey: roadmapKeys.points,
    queryFn: fetchRoadmapPoints,
    enabled,
    staleTime: 60 * 60 * 1000,
    retry: retryUnlessForbidden,
  });
}

export function useRoadmapAccess(orgId: string | null) {
  return useQuery({
    queryKey: roadmapKeys.access(orgId),
    queryFn: () => fetchRoadmapAccess(orgId as string),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/** The org's stored result; `data === null` once fetched means nothing has been computed yet. */
export function useRoadmapResult(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: roadmapKeys.result(orgId),
    queryFn: () => fetchRoadmapResult(orgId as string),
    enabled: !!orgId && enabled,
    staleTime: 60 * 1000,
    retry: retryUnlessForbidden,
  });
}

export function useRoadmapItems(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: roadmapKeys.items(orgId),
    queryFn: () => fetchRoadmapItems(orgId as string),
    enabled: !!orgId && enabled,
    staleTime: 60 * 1000,
  });
}

export function useRoadmapSnapshots(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: roadmapKeys.snapshots(orgId),
    queryFn: () => fetchRoadmapSnapshots(orgId as string),
    enabled: !!orgId && enabled,
    staleTime: 60 * 1000,
    retry: retryUnlessForbidden,
  });
}

const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

/** True when the org has no result, or its result is older than a day. */
export function resultIsStale(result: RoadmapResult | null | undefined, now: number = Date.now()): boolean {
  if (!result) return true;
  const ms = Date.parse(result.computedAt);
  return Number.isNaN(ms) || now - ms > STALE_AFTER_MS;
}

/**
 * Owners and admins keep the result fresh by opening the tab: when it is missing or a day old,
 * recompute (the coaching function, their own token) and refetch. Once per org per page load —
 * a failing recompute must not loop. No snapshot: the nightly run writes the day's row.
 */
export function useEnsureFreshState(
  orgId: string | null,
  args: { canEdit: boolean; result: RoadmapResult | null | undefined; isFetched: boolean },
): { refreshing: boolean } {
  const queryClient = useQueryClient();
  const attempted = useRef<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!orgId || !args.canEdit || !args.isFetched) return;
    if (attempted.current === orgId) return;
    if (!resultIsStale(args.result)) return;
    attempted.current = orgId;
    setRefreshing(true);
    recomputeRoadmapState(orgId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: roadmapKeys.result(orgId) });
      })
      .catch((err: unknown) => {
        console.warn('[roadmap] refresh failed', err);
      })
      .finally(() => setRefreshing(false));
  }, [orgId, args.canEdit, args.isFetched, args.result, queryClient]);

  return { refreshing };
}

/**
 * "Recalculate now" (owner/admin), and the refresh after a claim: derive, compute, store, then
 * refetch the result and the snapshots. Resolves false when the recompute failed.
 */
export function useRecalculateNow(orgId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!orgId) return false;
      await recomputeRoadmapState(orgId);
      return true;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: roadmapKeys.result(orgId) });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.snapshots(orgId) });
    },
  });
}

/** After a claim: the same recompute, refetch. Kept under its own name for the claim flow. */
export const useRefreshRoadmapState = useRecalculateNow;

// ── The stored result, which lands after an answer to the move does ────────

/** How often the stored result is re-read while the server's own store is still to come. */
const RESULT_POLL_MS = 2_000;

/** And for how long. Then the roadmap is re-read anyway: one recompute behind beats stale. */
const RESULT_POLL_WINDOW_MS = 30_000;

/**
 * One wait per organisation, SHARED by everyone waiting on it.
 *
 * The two answer paths ask for different things when the wait ends — the card's own answer
 * re-reads the roadmap page's queries, the roadmap page re-reads its queries, the 0–100 and the
 * move — so what is held per organisation is the SET of those callbacks, not one of them. It
 * used to be the timer alone: a second answer cleared the running timer, and the first caller's
 * `onDone` was never invoked, so that side of the screen kept the pre-answer result until
 * something else asked (and an in-flight attempt of the cleared chain could re-`set` the map and
 * leave the second caller's timer untracked, which is two polls for one organisation).
 *
 * So a second caller JOINS: its callback is added, the baseline drops to the earlier of the two
 * (`earlierStamp`), and the window extends to that caller's own thirty seconds. One poll per
 * organisation, and every callback registered against it runs exactly once when it ends.
 */
interface ResultWait {
  /** The pending re-read, when one is scheduled. */
  timer: ReturnType<typeof setTimeout> | null;
  /**
   * The window, enforced on its own clock.
   *
   * The deadline used to be checked only after a read came back, so a read that never came back
   * held every caller registered against this organisation for as long as it hung — and left the
   * entry in the map, which a later answer would then join instead of starting its own wait. The
   * window is a promise to the caller: thirty seconds, then it is told either way.
   */
  watchdog: ReturnType<typeof setTimeout> | null;
  /** What each caller owes its own screen. Run once each, when the wait ends. */
  onDones: Set<() => void>;
  /** The earliest stored-result stamp any caller answered against. */
  before: string | null;
  /** The latest deadline any caller asked for. */
  deadline: number;
  /**
   * This chain's token. An attempt reads it back off the map after its read and stops if the map
   * no longer holds this chain: a read that lands after the wait ended must not settle a second
   * time, and must not schedule a poll on top of whatever started since.
   */
  generation: number;
  /** The callbacks have run. */
  settled: boolean;
}

const resultPolls = new Map<string, ResultWait>();
let resultWaitGeneration = 0;

const BSS_LATEST_KEY = (orgId: string | null) => ['bss-calculation', 'latest', orgId] as const;

/**
 * The stored result's own `computedAt` as this browser last read it.
 *
 * `undefined` means the query holds nothing — it was never mounted (the roadmap flag is off for
 * this user, or the organisation cannot read one), so there is no timestamp to wait for and
 * nothing on screen that a wait would help. `null` means it has been read and the organisation
 * has no stored result yet.
 */
export function storedResultStamp(
  queryClient: QueryClient,
  orgId: string,
): string | null | undefined {
  const data = queryClient.getQueryData<RoadmapResult | null>(roadmapKeys.result(orgId));
  if (data === undefined) return undefined;
  return data?.computedAt ?? null;
}

/**
 * The earlier of two baselines. A shared wait ends on the first stored result newer than it, so
 * the earlier one ends the wait no later than either caller's own would: the later caller can be
 * told one recompute early, and re-reading a moment too soon beats a caller left waiting on a
 * result that has already been stored.
 *
 * `null` is "no stored result at all", which is earlier than any stamp. An unparseable stamp
 * counts as earliest too, because `resultAdvanced` reads it as already beaten.
 */
function earlierStamp(a: string | null, b: string | null): string | null {
  if (a === null || b === null) return null;
  const ta = Date.parse(a);
  if (Number.isNaN(ta)) return a;
  const tb = Date.parse(b);
  if (Number.isNaN(tb)) return b;
  return tb < ta ? b : a;
}

/** Has the stored result moved on from the one the answer was given against? */
function resultAdvanced(before: string | null, after: string | null | undefined): boolean {
  if (after === undefined || after === null) return false;
  if (before === null) return true;
  const a = Date.parse(after);
  if (Number.isNaN(a)) return false;
  const b = Date.parse(before);
  return Number.isNaN(b) || a > b;
}

/**
 * Wait for the result the server stores AFTER an answer, then re-read everything that answer
 * changed. ONE implementation for the two places an answer is given — the roadmap page's
 * question step below, and the next-move card (`use-current-move.ts`, which imports this module
 * and so cannot be imported back).
 *
 * An answer to today's move is replied to at the commit (hermes `finishCommitted`): the BSS
 * recalculation, the move recompute and `roadmap_store_result` all run after the reply, in that
 * order — the store is four or five round trips past the point the card's own move poll stops.
 * So the result read when the mutation settles is still the pre-answer one, and
 * `useRoadmapResult`'s sixty-second `staleTime` would keep it on the page and in Home's score
 * tile until something else asked. `before` is that result's stored timestamp as it was when the
 * answer went out, so this waits on the one fact it is about — a newer stored result — and
 * compares this browser's own two reads, never a clock.
 *
 * The store is the LAST step of the server's pass, so once it has moved the move and the 0–100
 * are final too. Nothing on screen waits for this: the answer is committed and the question is
 * already locked.
 *
 * `onDone` is what the caller owes its own screen when the wait ends — on the first newer stored
 * result, or on the window running out. The default is the roadmap page's: the org's roadmap
 * queries, the 0–100 and the card's move. A caller that arrives while a wait is already running
 * JOINS it (see `ResultWait`) rather than replacing it: one poll per organisation, and every
 * caller's `onDone` runs exactly once at the end of it.
 */
export function pollStoredResult(
  queryClient: QueryClient,
  orgId: string | null,
  before: string | null | undefined,
  onDone?: () => void,
): void {
  if (!orgId) return;
  const settle =
    onDone ??
    (() => {
      void queryClient.invalidateQueries({ queryKey: roadmapKeys.org(orgId) });
      void queryClient.invalidateQueries({ queryKey: BSS_LATEST_KEY(orgId) });
      void queryClient.invalidateQueries({ queryKey: currentMoveQueryKey(orgId) });
    });

  // No stored result is being read anywhere, so there is nothing THIS caller can wait on: it
  // re-reads and stops. A wait somebody else is running is left exactly as it is — it is about
  // a result query this caller cannot see, and the callers behind it are still owed their end.
  if (before === undefined) {
    settle();
    return;
  }

  const running = resultPolls.get(orgId);
  if (running !== undefined && !running.settled) {
    running.onDones.add(settle);
    running.before = earlierStamp(running.before, before);
    const deadline = Date.now() + RESULT_POLL_WINDOW_MS;
    if (deadline > running.deadline) {
      running.deadline = deadline;
      armWatchdog(orgId, running);
    }
    return;
  }

  const wait: ResultWait = {
    timer: null,
    watchdog: null,
    onDones: new Set([settle]),
    before,
    deadline: Date.now() + RESULT_POLL_WINDOW_MS,
    generation: ++resultWaitGeneration,
    settled: false,
  };
  resultPolls.set(orgId, wait);
  armWatchdog(orgId, wait);
  void attemptStoredResult(queryClient, orgId, wait);
}

/** (Re-)arm the window on the wait's own clock, so a read that hangs cannot outlast it. */
function armWatchdog(orgId: string, wait: ResultWait): void {
  if (wait.watchdog !== null) clearTimeout(wait.watchdog);
  wait.watchdog = setTimeout(
    () => finishResultWait(orgId, wait),
    Math.max(0, wait.deadline - Date.now()),
  );
}

/** The wait is over: every caller registered against it is told, once. */
function finishResultWait(orgId: string, wait: ResultWait): void {
  if (wait.settled) return;
  wait.settled = true;
  if (wait.timer !== null) {
    clearTimeout(wait.timer);
    wait.timer = null;
  }
  if (wait.watchdog !== null) {
    clearTimeout(wait.watchdog);
    wait.watchdog = null;
  }
  if (resultPolls.get(orgId) === wait) resultPolls.delete(orgId);
  const owed = [...wait.onDones];
  wait.onDones.clear();
  for (const done of owed) {
    try {
      done();
    } catch (err: unknown) {
      // One caller's re-read must not cost the next caller theirs.
      console.warn('[roadmap] result wait callback failed', err);
    }
  }
}

/** One read of the stored result, then: end the wait, or line up the next read. */
async function attemptStoredResult(
  queryClient: QueryClient,
  orgId: string,
  wait: ResultWait,
): Promise<void> {
  try {
    await queryClient.refetchQueries({ queryKey: roadmapKeys.result(orgId), exact: true });
  } catch (err: unknown) {
    // A failed read is not a reason to strand the callbacks: fall through to the checks below,
    // which either find the deadline passed or line up another read.
    console.warn('[roadmap] result read failed', err);
  }
  // The chain this read belongs to ended while the read was open (the window closed), or a
  // later answer's chain has taken its place. Either way this read is nobody's: settling on it
  // would be a second settle, and scheduling on it would poll over the live chain.
  const current = resultPolls.get(orgId);
  if (wait.settled || current === undefined || current.generation !== wait.generation) return;
  if (
    resultAdvanced(wait.before, storedResultStamp(queryClient, orgId)) ||
    Date.now() >= wait.deadline
  ) {
    finishResultWait(orgId, wait);
    return;
  }
  wait.timer = setTimeout(() => {
    wait.timer = null;
    void attemptStoredResult(queryClient, orgId, wait);
  }, RESULT_POLL_MS);
}

/**
 * The question step: answer a point's primary diagnostic question.
 *
 * The response carries the whole recomputed result, and it used to be written straight into the
 * result cache so the new score showed without waiting for a refetch. It cannot be: the edge
 * function returns what it stored, and `results.viewerHasFinanceAccess` is not part of a stored
 * result. Nothing in the edge functions writes that field — `get_roadmap_result` adds it per
 * caller, which is the point of it. So the cached copy carried `undefined`, `buildRoadmapView`
 * read `=== true` as false, and answering any question told the reader they had no finance
 * access: the KPI tile asked them to get Finance ticked in Team Members, and every pillar's
 * results line said the same. To an owner, about themselves.
 *
 * The same response also carries `stored`, which `roadmap_store_result` sets to false when it
 * refused the write because an equal-or-newer result was already there. Writing the refused
 * result into the cache as truth was the second half of the same mistake.
 *
 * So the result is not written here at all. `onSettled` invalidates it, the RPC answers, and the
 * screen shows a result that went through the only thing that knows who is asking.
 *
 * **An answer on today's open move is the move's response** (22 Sep 2026, `moveAnswered`). The
 * server records it on the prescription, the move rests until tomorrow's boundary, and the
 * reply comes at the commit with no result in it. Two things follow, both inside the mutation:
 *
 *   the lock    `get_current_move` is re-read and AWAITED before the mutation resolves. The row
 *               it brings back carries the response, which is what `useRoadmapMove` reads as
 *               rest, so the question is locked (StepList `restingAssetId`) by the time its
 *               "Saving…" state ends. Resolving first would hand back a live control for a move
 *               the server has already closed, and a second tap would be a plain diagnostic
 *               answer on a resting move.
 *   the result  the server stores it after the reply, so the page waits for a newer stored
 *               result (`pollStoredResult`) instead of trusting the read `onSettled` starts.
 */
export function useAnswerQuestion(orgId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ assetId, answer }: { assetId: string; answer: RoadmapAnswer }) => {
      // The stored result as it was BEFORE the answer: the baseline the wait compares against.
      const before = orgId ? storedResultStamp(queryClient, orgId) : null;
      const response = await answerRoadmapQuestion(orgId as string, assetId, answer);
      if (response.moveAnswered === true && orgId) {
        const moveKey = currentMoveQueryKey(orgId);
        // Cancel first: a read that started before the answer would land on top of it.
        await queryClient.cancelQueries({ queryKey: moveKey });
        await queryClient.refetchQueries({ queryKey: moveKey, exact: true });
        if (response.roadmapRefreshDeferred === true) pollStoredResult(queryClient, orgId, before);
      }
      return response;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: roadmapKeys.result(orgId) });
      queryClient.invalidateQueries({ queryKey: roadmapKeys.snapshots(orgId) });
      // The 0–100 moved too (the answer is a diagnostic answer).
      queryClient.invalidateQueries({ queryKey: BSS_LATEST_KEY(orgId) });
      // And the coaching move: the handler recomputes it before it stores the result (D6), so
      // Home's card is stale the moment this returns. It is also how a 409 from losing the move
      // to the card ends up locked: the refetched row carries the answer that won.
      queryClient.invalidateQueries({ queryKey: currentMoveQueryKey(orgId) });
    },
  });
}
